/**
 * Pitch-class and note-spelling helpers (FRE-185). Pure TypeScript: no React, no DOM.
 */

export type RootSlug = 'c' | 'cs' | 'd' | 'eb' | 'e' | 'f' | 'fs' | 'g' | 'ab' | 'a' | 'bb' | 'b';

export interface RootInfo {
  slug: RootSlug;
  /** ASCII display form: C C# D Eb E F F# G Ab A Bb B. */
  display: string;
  /** Pitch class 0..11. */
  pc: number;
  /** Persian solfege name. */
  fa: string;
}

/** Canonical order: C to B (AC-02). */
export const ROOTS: readonly RootInfo[] = [
  { slug: 'c', display: 'C', pc: 0, fa: 'دو' },
  { slug: 'cs', display: 'C#', pc: 1, fa: 'دو دیز' },
  { slug: 'd', display: 'D', pc: 2, fa: 'ر' },
  { slug: 'eb', display: 'Eb', pc: 3, fa: 'می بمل' },
  { slug: 'e', display: 'E', pc: 4, fa: 'می' },
  { slug: 'f', display: 'F', pc: 5, fa: 'فا' },
  { slug: 'fs', display: 'F#', pc: 6, fa: 'فا دیز' },
  { slug: 'g', display: 'G', pc: 7, fa: 'سل' },
  { slug: 'ab', display: 'Ab', pc: 8, fa: 'لا بمل' },
  { slug: 'a', display: 'A', pc: 9, fa: 'لا' },
  { slug: 'bb', display: 'Bb', pc: 10, fa: 'سی بمل' },
  { slug: 'b', display: 'B', pc: 11, fa: 'سی' }
];

const ROOT_BY_SLUG = new Map<string, RootInfo>(ROOTS.map((r) => [r.slug, r]));
const ROOT_BY_PC = new Map<number, RootInfo>(ROOTS.map((r) => [r.pc, r]));

export const isRootSlug = (s: string): s is RootSlug => ROOT_BY_SLUG.has(s);
export const getRoot = (slug: RootSlug): RootInfo => ROOT_BY_SLUG.get(slug)!;
export const rootFromPc = (pc: number): RootInfo => ROOT_BY_PC.get(((pc % 12) + 12) % 12)!;

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
const NATURAL_PC = [0, 2, 4, 5, 7, 9, 11];

/** Pitch class of a spelled note such as `C#`, `Eb`, `Bbb`, `F##`. Unicode sharp and flat are accepted. */
export function notePc(note: string): number | null {
  const m = /^([A-Ga-g])([#b♯♭]*)$/.exec(note.trim());
  if (!m) return null;
  let pc = NATURAL_PC[LETTERS.indexOf(m[1].toUpperCase() as (typeof LETTERS)[number])];
  for (const ch of m[2]) pc += ch === '#' || ch === '♯' ? 1 : -1;
  return ((pc % 12) + 12) % 12;
}

/** Maps `A#`, `Db`, `Bb`, `a#` ... to the root slug. `parseRoot('A#') === 'bb'`, `parseRoot('Db') === 'cs'`. */
export function parseRoot(input: string): RootSlug | null {
  const m = /^([A-Ga-g])(#|b|♯|♭)?$/.exec(input.trim());
  if (!m) return null;
  const pc = notePc(m[1] + (m[2] ?? ''));
  return pc === null ? null : rootFromPc(pc).slug;
}

export type IntervalName =
  | '1' | 'b2' | '2' | 'b3' | '3' | '4' | '#4' | 'b5' | '5' | '#5' | 'b6' | '6' | 'bb7' | 'b7' | '7' | '9';

export interface IntervalInfo {
  semitones: number;
  degree: number;
}

export const INTERVALS: Readonly<Record<IntervalName, IntervalInfo>> = {
  '1': { semitones: 0, degree: 1 },
  b2: { semitones: 1, degree: 2 },
  '2': { semitones: 2, degree: 2 },
  b3: { semitones: 3, degree: 3 },
  '3': { semitones: 4, degree: 3 },
  '4': { semitones: 5, degree: 4 },
  '#4': { semitones: 6, degree: 4 },
  b5: { semitones: 6, degree: 5 },
  '5': { semitones: 7, degree: 5 },
  '#5': { semitones: 8, degree: 5 },
  b6: { semitones: 8, degree: 6 },
  '6': { semitones: 9, degree: 6 },
  bb7: { semitones: 9, degree: 7 },
  b7: { semitones: 10, degree: 7 },
  '7': { semitones: 11, degree: 7 },
  '9': { semitones: 14, degree: 9 }
};

/** Spells `interval` above the root: one letter per degree, accidentals in ASCII (`#`, `##`, `b`, `bb`). */
export function spell(rootSlug: RootSlug, interval: IntervalName): string {
  const root = getRoot(rootSlug);
  const { semitones, degree } = INTERVALS[interval];
  const letterIdx = (LETTERS.indexOf(root.display[0] as (typeof LETTERS)[number]) + degree - 1) % 7;
  const target = (root.pc + semitones) % 12;
  const diff = ((((target - NATURAL_PC[letterIdx]) % 12) + 18) % 12) - 6;
  const acc = diff === 0 ? '' : diff > 0 ? '#'.repeat(diff) : 'b'.repeat(-diff);
  return LETTERS[letterIdx] + acc;
}

/** Pitch class of the chord tone `interval` above the root. */
export const intervalPc = (rootSlug: RootSlug, interval: IntervalName): number =>
  (getRoot(rootSlug).pc + INTERVALS[interval].semitones) % 12;
